import { validate } from 'uuid'

import { getGLTF } from '@/services/queries/cads'
import { GLTFObject } from '@/lib/cad/GLTFObject'
import { getCadVersion, ImageData, updateView, View } from '@/lib/api/client'

export const olderThanV1 = (imageData: ImageData) =>
  !imageData.version || imageData.version < 1

const explosionsNeedsUpdate = (imageData: ImageData) =>
  !!imageData.explosions && !!imageData.needsUpdate

export const validateImageDataVersion = async (
  views: View[],
  csrf?: string,
): Promise<string[]> => {
  if (!views.length) return []

  // fetch & process gltf if it will be needed
  let gltf: GLTFObject | null
  if (checkShouldUpdate(views)) {
    const cadVersion = await getCadVersion(views[0].cad_version)
    if (cadVersion.download_url) {
      const gltfData = await getGLTF({
        downloadUrl: cadVersion.download_url,
        onLoadingProgress: () => {},
      })
      gltf = gltfData.gltf
      gltf.generateObjectMaps(cadVersion.assembly_tree as any)
    }
  }

  // validate image data
  const updatedViewIds: string[] = []
  await Promise.all(
    views.map(async (view) => {
      let shouldUpdateView = false
      if (view.source_type !== 'screenshot') return
      const imageData = view.image_data

      // validate image data below version 1
      if (olderThanV1(imageData)) {
        shouldUpdateView = true
        addExplosionsTranslations(imageData)
        if (gltf) updatePartIds(imageData, gltf)
        imageData.version = 1
        console.debug(`${view.name} image data upgraded to v1`)
      }

      // update explosions if needsUpdate then reset needsUpdate flag
      if (explosionsNeedsUpdate(imageData) && gltf) {
        shouldUpdateView = true
        updateExplosionsPositions(imageData, gltf)
        imageData.needsUpdate = false
      }

      // update view with new image data
      if (shouldUpdateView) {
        if (!view.id) throw new Error('no view id - unable to update view')
        const newView = { ...view, image_data: imageData }
        await updateView(
          view.id,
          {
            ...newView,
            image_data: newView.image_data,
            document_page_id: view.document_page ?? null,
          },
          csrf,
        )
        updatedViewIds.push(view.id)
      }
    }),
  )
  return updatedViewIds
}

/**
 * Check if any job has an image data version less than the specified versions
 * or if any job has image data that has needsUpdate flag set to true
 */
const checkShouldUpdate = (views: View[]) => {
  let shouldUpdate = false
  for (const view of views) {
    if (view.source_type !== 'screenshot') continue

    const imageData = view.image_data
    if (olderThanV1(imageData) || explosionsNeedsUpdate(imageData)) {
      shouldUpdate = true
      break
    }
  }

  return shouldUpdate
}

/**
 * Updates the positions of the explosions in the image data, in the event that
 * an exploded part has been moved in the new CAD version
 */
const updateExplosionsPositions = (imageData: ImageData, gltf: GLTFObject) => {
  if (imageData.explosions) {
    Object.keys(imageData.explosions).forEach((partId) => {
      const object = validate(partId)
        ? gltf.getObjectByUUID(partId)
        : gltf.getObjectByName(partId)

      if (!object) return

      const explosion = imageData.explosions[partId]
      explosion.originalPosition = object.position.toArray()
      explosion.position = [
        explosion.originalPosition[0] + explosion.translation[0],
        explosion.originalPosition[1] + explosion.translation[1],
        explosion.originalPosition[2] + explosion.translation[2],
      ]
    })
  }
}

/**
 * Introduced in imageData v1 on 3/31/2025
 * Adds the translation property to the explosions in the image data, if it doesn't exist
 */
const addExplosionsTranslations = (imageData: ImageData) => {
  if (imageData.explosions) {
    Object.keys(imageData.explosions).forEach((partId) => {
      const explosion = imageData.explosions[partId]
      if (!explosion.translation) {
        explosion.translation = [
          explosion.position[0] - explosion.originalPosition[0],
          explosion.position[1] - explosion.originalPosition[1],
          explosion.position[2] - explosion.originalPosition[2],
        ]
      }
    })
  }
}

/**
 * Introduced in imageData v1 on 3/31/2025
 * Cleans up the image data by updating the part IDs to be UUIDs
 */
const updatePartIds = (imageData: ImageData, gltf: GLTFObject) => {
  if (imageData.explosions) {
    Object.keys(imageData.explosions).forEach((partId) => {
      if (!validate(partId)) {
        const object = gltf.getObjectByName(partId)
        const uuid = object ? gltf.objectPartMap.get(object)?.uuid : null
        if (uuid) {
          imageData.explosions[uuid] = imageData.explosions[partId]
          delete imageData.explosions[partId]
        } else {
          console.error(`Could not update explosions data for ${partId}`)
        }
      }
    })
  }

  if (imageData.colorMap) {
    Object.keys(imageData.colorMap).forEach((partId) => {
      if (!validate(partId)) {
        const object = gltf.getObjectByName(partId)
        const uuid = object ? gltf.objectPartMap.get(object)?.uuid : null
        if (uuid) {
          imageData.colorMap[uuid] = imageData.colorMap[partId]
          delete imageData.colorMap[partId]
        } else {
          console.error(`Could not update colorMap data for ${partId}`)
        }
      }
    })
  }

  if (imageData.transparentParts) {
    imageData.transparentParts.map((partId) => {
      if (!validate(partId)) {
        const object = gltf.getObjectByName(partId)
        const uuid = object ? gltf.objectPartMap.get(object)?.uuid : null
        if (uuid) {
          imageData.transparentParts.push(uuid)
          imageData.transparentParts = imageData.transparentParts.filter(
            (id) => id !== partId,
          )
        } else {
          console.error(`Could not update transparentParts data for ${partId}`)
        }
      }
    })
  }

  if (imageData.exclusiveCrossSectionMap) {
    imageData.exclusiveCrossSectionMap.map((clippingPlaneParts) => {
      clippingPlaneParts.map((partId) => {
        if (!validate(partId)) {
          const object = gltf.getObjectByName(partId)
          const uuid = object ? gltf.objectPartMap.get(object)?.uuid : null
          if (uuid) {
            clippingPlaneParts.push(uuid)
            clippingPlaneParts = clippingPlaneParts.filter(
              (id) => id !== partId,
            )
          } else {
            console.error(`Could not update crossSectionMap data for ${partId}`)
          }
        }
      })
    })
  }
}
